import axios from 'axios';
import { create } from 'zustand';
// Varsayımsal Tipler ve Servisler
import { IntegrationApplication, IntegrationQueryTemplate } from '../types/integration'; 
import { createApiStore, GenericStore, ListApiResult } from './genericStore'; // Generic Store'unuzun yolu
import { ListQuery, QueryParams } from '../types';
import api from '../service/api'; 
import { toast } from 'react-toastify';


// -------------------------------------------------------------
// 1. ENDPOINT TANIMI
// -------------------------------------------------------------
const endpointApplication = '/integration/application';
const endpointQuery = '/integration/query';

// -------------------------------------------------------------
// 2. TEMEL GENERIC STORE VE TİP TANIMI
// -------------------------------------------------------------

// IntegrationApplication için temel generic store'u oluştur (Sadece initial state ve tipleri almak için)
const useApplicationStore = createApiStore<IntegrationApplication>(endpointApplication);


// Application Store'un tipini alıyoruz ve üzerine Queries için ek durumları/metotları ekliyoruz
type CustomApplicationStore = GenericStore<IntegrationApplication> & {
    // Queries için ek durum ve metotlar
    queries: IntegrationQueryTemplate[];
    queryTotal: number;
    queryLoading: boolean;

    // Sorgu Şablonu CRUD işlemleri (Imza tanımı)
    fetchQueriesDynamic: (params: QueryParams) => Promise<ListApiResult<IntegrationQueryTemplate>>;
    createQuery: (data: Partial<IntegrationQueryTemplate>) => Promise<IntegrationQueryTemplate>;
    updateQuery: (id: string | number, data: Partial<IntegrationQueryTemplate>) => Promise<IntegrationQueryTemplate>;
    deleteQuery: (id: string | number) => Promise<any>;
};


// -------------------------------------------------------------
// 3. ENTEGRASYON ANA STORE TANIMI
// -------------------------------------------------------------

export const useIntegrationStore = create<CustomApplicationStore>((set, get) => {
    
    // Base Store'un initial state'ini alıyoruz (data: [], total: 0, loading: false vb.)
    const baseState = useApplicationStore.getState();

    let activeAppAbortController: AbortController | null = null;
    let latestAppRequestId = 0;

    let activeQueryAbortController: AbortController | null = null;
    let latestQueryRequestId = 0;

    // =========================================================
    // A. OVERRIDE EDİLEN METOTLAR (Application CRUD & Listeleme)
    // =========================================================
    
    // Uygulama Listesini Çekme (Veriyi kendi store'umuzun state'ine yazar)
    const fetchDataDynamic = async (params?: QueryParams) => {
         if (activeAppAbortController) {
             activeAppAbortController.abort();
         }
         const currentController = new AbortController();
         activeAppAbortController = currentController;
         const currentRequestId = ++latestAppRequestId;

         const finalParams = params || get().queryParams;
         set({ loading: true }); // Kendi 'loading' state'ini güncelle
         
         const urlParams = { 
             pageIndex: finalParams.pageIndex, 
             pageSize: finalParams.pageSize,
             showDeleted: false, // Varsayılan olarak silinmişleri gösterme       
         };

         // API'ye gönderilecek ListQuery objesini hazırlama
         const requestBody: ListQuery = {
             sort: finalParams.sort || [], 
             filter: finalParams.filter 
               ? { logic: "and", filters: [finalParams.filter] } 
               : { logic: "and", filters: [] },
    
         };

         try {
             const res = await api.post<ListApiResult<IntegrationApplication>>(`${endpointApplication}/DynamicQuery`, requestBody, { 
                params: urlParams,
                headers: { "x-silent": "true" },
                signal: currentController.signal
             });
             
             if (currentRequestId !== latestAppRequestId || currentController.signal.aborted) {
                 return { items: [], total: 0 };
             }

             set({
                 data: res.data.items || [],      // <-- KRİTİK: Kendi 'data' (applications) state'ini güncelle
                 total: res.data.total || 0,
                 loading: false,
             });
             // QueryParams'ı da güncelle
             get().setQueryParams(finalParams);
             return res.data; 
         } catch (err: any) {
             if (axios.isCancel(err) || err?.name === 'CanceledError' || currentRequestId !== latestAppRequestId || currentController.signal.aborted) {
                 return { items: [], total: 0 };
             }
             toast.error("Uygulamalar çekilirken hata oluştu.");
             set({ loading: false });
             throw err;
         }
    };
    
    // Yeni Uygulama Oluşturma
    const createItem = async (data: Partial<IntegrationApplication>) => {
        const res = await api.post<IntegrationApplication>(endpointApplication, data);
        await fetchDataDynamic(get().queryParams); // Başarılıysa listeyi yenile
        return res.data;
    }
    
    // Uygulama Güncelleme
    const updateItem = async (id: string | number, data: Partial<IntegrationApplication>) => {
        const res = await api.put<IntegrationApplication>(`${endpointApplication}/${id}`, data);
        await fetchDataDynamic(get().queryParams); 
        return res.data;
    }
    
    // Uygulama Silme
    const deleteItem = async (id: string | number) => {
        await api.delete(`${endpointApplication}/${id}`);
        await fetchDataDynamic(get().queryParams); 
    }

    // =========================================================
    // B. QUERY METOTLARI (Sorgu Şablonu CRUD & Listeleme)
    // =========================================================
    
    const fetchQueriesDynamic = async (params?: QueryParams) => {
        if (activeQueryAbortController) {
            activeQueryAbortController.abort();
        }
        const currentController = new AbortController();
        activeQueryAbortController = currentController;
        const currentRequestId = ++latestQueryRequestId;

        const { queryParams } = get();
        const finalParams = params || queryParams;
        set({ queryLoading: true });
        
        const urlParams = { 
            pageIndex: finalParams.pageIndex, 
            pageSize: finalParams.pageSize,
            showDeleted: false,       
        };

        const requestBody: ListQuery = {
            sort: finalParams.sort || [], 
            filter: finalParams.filter 
              ? { logic: "and", filters: [finalParams.filter] } 
              : { logic: "and", filters: [] }, 
        };

        try {
            const res = await api.post<ListApiResult<IntegrationQueryTemplate>>(`${endpointQuery}/DynamicQuery`, requestBody, { 
                params: urlParams,
                headers: { "x-silent": "true" },
                signal: currentController.signal
            });
            
            if (currentRequestId !== latestQueryRequestId || currentController.signal.aborted) {
                return { items: [], total: 0 };
            }

            set({
                queries: res.data.items || [],
                queryTotal: res.data.total || 10,
                queryLoading: false,
            });
            return res.data; 
        } catch (err: any) {
            if (axios.isCancel(err) || err?.name === 'CanceledError' || currentRequestId !== latestQueryRequestId || currentController.signal.aborted) {
                return { items: [], total: 0 };
            }
            toast.error("Sorgu şablonları çekilirken hata oluştu.");
            set({ queryLoading: false });
            throw err;
        }
    };

    const createQuery = async (data: Partial<IntegrationQueryTemplate>) => {
         const res = await api.post<IntegrationQueryTemplate>(endpointQuery, data);
         return res.data;
    };

    const updateQuery = async (id: string | number, data: Partial<IntegrationQueryTemplate>) => {
        const res = await api.put<IntegrationQueryTemplate>(`${endpointQuery}/${id}`, data);
        return res.data;
    };
    
    const deleteQuery = async (id: string | number) => {
        await api.delete(`${endpointQuery}/${id}`);
    };


    // =========================================================
    // C. RETURN (Store İçeriği)
    // =========================================================

    return {
        // Generic Store'dan gelen tüm DURUMLAR (initial state)
        ...baseState, // data: [], total: 0, loading: false, queryParams: { ... }

        // Query'lere özel durumlar
        queries: [],
        queryTotal: 10,
        queryLoading: false,

        // OVERRIDE EDİLMİŞ/YENİDEN TANIMLANMIŞ METOTLAR (Applications)
        fetchDataDynamic: fetchDataDynamic,
        createItem: createItem,
        updateItem: updateItem,
        deleteItem: deleteItem,
        
        // Sadece Referans Olarak Alınan Metotlar (Generic Store'dan alınabilir)
        fetchData: baseState.fetchData, 
        setQueryParams: baseState.setQueryParams, 

        // SORGULAR (QUERY) İÇİN ÖZEL METOTLAR
        fetchQueriesDynamic: fetchQueriesDynamic,
        createQuery: createQuery,
        updateQuery: updateQuery,
        deleteQuery: deleteQuery,
    };
});